<template lang="pug">
  v-flex(d-flex)
    v-snackbar(:value="recipientsError" color="error")
      div {{ recipientsErrorMessage }}

    pagination-list-modal(
      v-if="showContactsDialog"
      v-model="showContactsDialog"
      :items="toSelected.slice(skip, skip + limit)"
      :total="toSelected.length"
      :limit="limit"
      :skip="skip"
      title="Email Recipients"
      description="These contacts will receive the email:"
      @close="closeContactsDialog"
      @setPage="setContactsPage"
      @setPageLimit="setContactsPageLimit")
        template(slot="items" slot-scope="{ item }")
          v-layout(row)
            v-checkbox.pa-0.ma-0(:input-value="true" color='primary' @click="removeItem(item)" hide-details :style="{'flex':0}") 
            div {{displayContact(item)}}

    v-container.pb-0.mb-0
      v-layout(row)
        v-flex(xs12 :class="{'pt-1 pb-0': hideAdditionalFields}")
          //- Inject the following line to inject ad hoc adding email
          //- @keyup.enter="searchContactResult"
          v-autocomplete.pt-0(
            v-model="toSelected"
            label="To:"
            item-value="id"
            :items="allContacts.concat(toSelected)"
            :filter="filter"
            :search-input.sync="toSearchValue"
            :item-text="displayContact"
            :prepend-icon="icon"
            :disabled="disableRecipients"
            dense
            clearable
            return-object
            multiple
            chips
            deletable-chips
          )
            template(slot="no-data")
              v-list-tile
                v-list-tile-content
                  v-list-tile-title {{ displayNone(toSearchValue) }}
            template(slot="selection" slot-scope="data")
              v-chip(v-if="data.index < 5") {{ displayContact(data.item) }}
              v-chip.clickable(v-if="data.index === 5" @click.stop="openContactsDialog") + {{ invisibleContacts.length }} more
            template(slot="item" slot-scope="data")
              v-checkbox.disable-click(:input-value="isSelected(data.item)" :style="{'flex':0}" color='primary')
              div {{ displayContact(data.item) }}
      v-layout(v-if="!showCC || !showBCC").px-4.below-title
        v-btn(flat color="primary" v-if="!showCC" @click="showCC = true") Add CC
        v-btn(flat color="primary" v-if="!showBCC" @click="showBCC = true") Add BCC
      v-layout(row v-show="showCC")
        v-flex(xs12 :class="{'py-0': hideAdditionalFields}")
          v-autocomplete.pt-0(
            v-model="ccSelected"
            label="CC:"
            :items="allContacts.concat(ccSelected)"
            :filter="filter"
            :search-input.sync="ccSearchValue"
            :item-text="displayContact"
            item-value="email"
            dense
            :prepend-icon="icon"
            clearable
            return-object
            multiple
            chips
            deletable-chips
          )
            template(slot="no-data")
              v-list-tile
                v-list-tile-content
                  v-list-tile-title {{ displayNone(ccSearchValue) }}
      v-layout(row v-show="showBCC")
        v-flex(xs12 :class="{'py-0': hideAdditionalFields}")
          v-autocomplete.pt-0(
            v-model="bccSelected"
            label="BCC:"
            :items="allContacts.concat(bccSelected)"
            :filter="filter"
            :prepend-icon="icon"
            :search-input.sync="bccSearchValue"
            :item-text="displayContact"
            item-value="email"
            dense
            clearable
            return-object
            multiple
            chips
            deletable-chips
          )
            template(slot="no-data")
              v-list-tile
                v-list-tile-content
                  v-list-tile-title {{ displayNone(bccSearchValue) }}
</template>
<script>
import { mapActions, mapState, createNamespacedHelpers } from 'vuex';
import { debounce } from '@/Core/utilities';
import { checkEmailValidity } from '@/Core/utilities/checkEmailValidity';
import { hasMoreItems } from '@/Core/utilities/arrayComparison';
import { COMMUNICATION_TYPE } from '@/Core/config/communicationType';
import PaginationListModal from '@/Core/components/PaginationListModal'

const { mapGetters: mapUserInformationGetters } = createNamespacedHelpers(
  'userInformation',
);

export default {
  components: {
    PaginationListModal
  },
  props: {
    confirmRecipientClear: {
      type: Boolean,
    },
    hideAdditionalFields: {
      type: Boolean,
      default: false,
    },
    clearOnDestroy: {
      type: Boolean,
      default: true,
    },
    disableRecipients: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      icon: 'person',
      toSelected: [],
      ccSelected: [],
      bccSelected: [],
      toSearchValue: '',
      ccSearchValue: '',
      bccSearchValue: '',
      showCC: !this.hideAdditionalFields,
      showBCC: !this.hideAdditionalFields,
      showContactsDialog: false,
      limit: 15,
      skip: 0,
      page: 1,
    };
  },
  computed: {
    ...mapState({
      allContacts: state => state.recipients.allContacts,
      initialToSelected: state => state.recipients.toSelected,
      initialCcSelected: state => state.recipients.ccSelected,
      initialBccSelected: state => state.recipients.bccSelected,
      searchString: state => state.recipients.searchString,
      recipientsError: state => state.recipients.recipientsError,
      recipientsErrorMessage: state => state.recipients.recipientsErrorMessage,
      recipientsInProgress: state => state.recipients.recipientsInProgress,
    }),
    ...mapUserInformationGetters(['userHasFeatureFlag']),
    visibleContacts() {
      return this.toSelected.slice(0, 5);
    },
    invisibleContacts() {
      return this.toSelected.slice(5);
    },
  },
  mounted() {
    this.toSelected = this.initialToSelected;
    this.ccSelected = this.initialCcSelected;
    this.bccSelected = this.initialBccSelected;
  },
  beforeDestroy() {
    if(this.clearOnDestroy) {
      this.setToSelected([]);
      this.setCcSelected([]);
      this.setBccSelected([]);
    }
  },
  watch: {
    confirmRecipientClear(value) {
      if (value) {
        this.clearAll();
        this.$emit('reset-confirm-recipient-clear');
      }
    },
    toSearchValue(value) {
      if (value && value.length > 2) {
        debounce(this.setSearchString, [this.formatSearchString(value)]);
      } else {
        debounce(this.setSearchString, [this.formatSearchString('')]);
      }
    },
    ccSearchValue(value) {
      if (value && value.length > 2) {
        debounce(this.setSearchString, [this.formatSearchString(value)]);
      } else {
        debounce(this.setSearchString, [this.formatSearchString('')]);
      }
    },
    bccSearchValue(value) {
      if (value && value.length > 2) {
        debounce(this.setSearchString, [this.formatSearchString(value)]);
      } else {
        debounce(this.setSearchString, [this.formatSearchString('')]);
      }
    },
    /**
     * Stores the resired recipients on the Vuex store
     * Clears the search value if there a new tag being added
     * Otherwise, it means that the user is removing a selected tag,
     * at which don't clear the search value
     */
    toSelected(currentToRecipients, prevToRecipients) {
      if (!hasMoreItems(prevToRecipients, currentToRecipients)) this.toSearchValue = '';
      this.setToSelected(currentToRecipients);
    },
    ccSelected(currentCcRecipients, prevCcRecipients) {
      if (!hasMoreItems(prevCcRecipients, currentCcRecipients)) this.ccSearchValue = '';
      this.setCcSelected(currentCcRecipients);
    },
    bccSelected(currentBccRecipients, prevBccRecipients) {
      if (!hasMoreItems(prevBccRecipients, currentBccRecipients)) this.bccSearchValue = '';
      this.setBccSelected(currentBccRecipients);
    },
    showCC(newValue) {
      if (newValue) this.$emit('input-is-added');
    },
    showBCC(newValue) {
      if (newValue) this.$emit('input-is-added');
    },
  },
  methods: {
    ...mapActions('recipients', [
      'setSearchString',
      'setToSelected',
      'setCcSelected',
      'setBccSelected',
    ]),
    /**
     * Parses the serach value string into an object that the recipients store needs
     * @param {String} searchValue is the intended search value
     * @returns {Object} the formatted searchValue object to be given to the vuex store
     */
    formatSearchString(searchValue) {
      return {
        searchValue,
        type: COMMUNICATION_TYPE.EMAIL,
      };
    },
    clearAll() {
      this.toSelected = [];
      this.ccSelected = [];
      this.bccSelected = [];
    },
    /*
     * Sets the desired display value for each contact
     */
    displayContact: item => {
      if (item.first_name && item.last_name) {
        return `${item.first_name || ''} ${item.last_name || ''} (${item.email})`;
      }
      return `${item.email}`;
    },
    /**
     * @param {Object} [item] the current item we're iterating on
     * @param {String} [queryText] The search string that the user inputs
     * @param {String} [itemText] The stringified item
     *
     * @returns {Boolean}  It returns whether each item should be included
     *                     the resulting list
     *
     * Custom front-end filter in order to filter out the people with sent emails.
     * This is necessary because we combine the selected contacts to the all contacts.
     * Without the combination, the selected items will not be visually present.
     */
    filter(item, queryText, itemText) {
      if (item.header) return false;

      const text = itemText != null ? itemText : '';
      const query = queryText != null ? queryText : '';

      return (
        text
          .toString()
          .toLowerCase()
          .indexOf(query.toString().toLowerCase()) > -1
      );
    },
    /**
     * Appends the desired ad hoc email to the list of intended recipients
     * if the user inputs a valid email
     */
    searchContactResult() {
      if (this.toSearchValue && checkEmailValidity(this.toSearchValue)) {
        const toSelectedCopy = [].concat(this.toSelected);
        toSelectedCopy.push({
          id: null,
          first_name: null,
          last_name: null,
          email: `${this.toSearchValue}`,
        });
        this.toSelected = toSelectedCopy;
      }
    },
    /**
     * Returns the correct string if there is no contact that matches
     * the given search string.
     */
    displayNone(value) {
      if (value && value.length > 2) {
        // TODO NA: Add this back when ad hoc email addition is aadded back
        // if (checkEmailValidity(value)) {
        //   return `No results matching ${value}.
        //     Press enter to add email to recipient list.`;
        // }
        // return 'Please enter a valid email';
        return 'No results';
      }
      return 'Please enter at least 3 letters to search contacts';
    },
    openContactsDialog() {
      this.showContactsDialog = true;
    },
    closeContactsDialog() {
      this.showContactsDialog = false;
    },
    setContactsPage(page) {
      this.page = page;
      this.skip = (page - 1) * this.limit;
    },
    setContactsPageLimit(limit) {
      this.limit = limit;
      this.setContactsPage(1)
    },
    isSelected(item) {
      return this.toSelected.map(value => value.id).includes(item.id);
    },
    removeItem(item) {
      const index = this.toSelected.findIndex(selectedItem => selectedItem.id === item.id);
      if (index !== -1) {
        this.toSelected = [
        ...this.toSelected.slice(0, index),
        ...this.toSelected.slice(index + 1),
      ];
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.below-title {
  margin-top: -22px !important;
}

.clickable /deep/ .v-chip__content {
  cursor: pointer !important;
  color: var(--v-primary-base);
}

.disable-click {
  pointer-events: none !important;
}
</style>
